import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import './screens/screens.css';

// API サービスをインポート
import * as api from '../services/api';

// スクリーンコンポーネント
import HomeScreen from './screens/HomeScreen';
import AppListScreen from './screens/AppListScreen';
import AppScreen from './screens/AppScreen';
import LoadingScreen from './screens/LoadingScreen';
import NotFoundScreen from './screens/NotFoundScreen';
import LoginScreen from './screens/LoginScreen';
import UnauthenticatedScreen from './screens/UnauthenticatedScreen';
import PlanScreen from './screens/PlanScreen';
import BillingSuccessScreen from './screens/BillingSuccessScreen';
import BillingCancelScreen from './screens/BillingCancelScreen';
import FooterNavigation from './FooterNavigation';

// カスタムフック
import { useApp } from '../hooks/useApp';
import { useAuth } from './AuthProvider';
import { usePageTracking } from '../hooks/useAnalytics';
import * as apiSvc from '../services/api';

const ApptukuApp = () => {
  // 認証情報を取得
  const {
    isLoggedIn,
    liffLoading,
    liffError,
    isInClient,
    shareContent,
    login,
    logout,
    userId,
    authCheckDone,
    profile // Add profile here
  } = useAuth();

  // URL検索パラメータ
  const [searchParams] = useSearchParams();
  const appId = searchParams.get('id');
  const testMode = searchParams.get('test');

  // テストモードがCreatingScreenの場合、アプリ作成中の状態をシミュレート
  const isTestCreating = testMode === 'CreatingScreen';

  // URLパスを取得して課金画面を判定
  const currentPath = window.location.pathname;
  const isBillingSuccess = currentPath.includes('/billing/success');
  const isBillingCancel = currentPath.includes('/billing/cancel');
  const isBillingPath = currentPath.includes('/billing') && !isBillingSuccess && !isBillingCancel;

  // 状態定義
  const initialScreen = isBillingSuccess ? 'billingSuccess' :
                        isBillingCancel ? 'billingCancel' :
                        isBillingPath ? 'plan' :
                        testMode === 'AppListScreen' ? 'appList' :
                        testMode === 'NotFoundScreen' ? 'notFound' :
                        testMode === 'LoadingScreen' ? 'loading' :
                        (appId ? 'loading' : 'home');
  const [currentScreen, setCurrentScreen] = useState(initialScreen);
  const [recipe, setRecipe] = useState('');
  const [isCreatingApp, setIsCreatingApp] = useState(isTestCreating || false);
  
  // Analytics: ページ遷移の自動追跡
  usePageTracking(currentScreen);
  const [currentRunId, setCurrentRunId] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [redirectAppId, setRedirectAppId] = useState(appId || null);
  const [completionMessage, setCompletionMessage] = useState(null);
  const [failureMessage, setFailureMessage] = useState(null);
  const [showLongWaitModal, setShowLongWaitModal] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const reconcilingRef = useRef(false);
  useEffect(() => { reconcilingRef.current = reconciling; }, [reconciling]);

  // 二重実行防止用の同期的なフラグ（useRefで即座に更新）
  const isCreatingRef = useRef(false);

  // creatingAppId を最新に保持し、isCreatingAppと同期させるヘルパ
  const creatingIdRef = useRef(localStorage.getItem('creatingAppId'));
  const setCreatingAppIdLS = (id) => {
    try {
      if (id) localStorage.setItem('creatingAppId', id);
      else localStorage.removeItem('creatingAppId');
    } catch (_) {}
    creatingIdRef.current = id || null;
    setIsCreatingApp(!!id);
  };
  const [publicName, setPublicName] = useState(() => {
    try {
      return localStorage.getItem('user_public_name') || '匿名';
    } catch (_) {
      return '匿名';
    }
  });

  // 初期プロフィールが取得でき、かつ公開表示名が未設定ならLINE名を初期値として保存
  useEffect(() => {
    try {
      const hasOverride = !!localStorage.getItem('user_public_name');
      if (!hasOverride && profile?.displayName) {
        localStorage.setItem('user_public_name', profile.displayName);
        setPublicName(profile.displayName);
      }
    } catch (_) {}
  }, [profile?.displayName]);

  // API から公開表示名を取得（端末を跨いでも同じ表示名を使う）
  useEffect(() => {
    const loadPublicName = async () => {
      if (!userId) return;
      try {
        const name = await apiSvc.getUserPublicName(userId);
        if (name && name !== publicName) {
          localStorage.setItem('user_public_name', name);
          setPublicName(name);
        }
      } catch (_) {
        // ignore
      }
    };
    loadPublicName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const savePublicName = async (name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return false;
    if (trimmed.length > 24) return false;
    try {
      localStorage.setItem('user_public_name', trimmed);
      setPublicName(trimmed);
      // API経由で保存（将来の端末間同期用）
      if (userId) {
        await apiSvc.setUserPublicName(userId, trimmed);
      }
      return true;
    } catch (_) {
      return false;
    }
  };
  
  // LPから抽出した20個のサンプルアプリデータ
  const allSampleApps = [
    { id: '1', title: 'みんなでランキング投票アプリ', recipe: 'クラスで人気の曲、好きな映画、イケメン俳優などをみんなで投票してリアルタイムランキングが見れるアプリ\n投票は1つの項目に対して一人一回で限定したい', url: "https://miniapplify.web.app/?id=Sc75env8bEytfNqEtbAy" },
    { id: '2', title: '秒速多数決メーカー', recipe: 'みんなでアンケートが取れるアプリを作って！\n\nまず「お題」（例えば「放課後どこ行く？」とか）を決められて、そのあとに「カラオケ」とか「サイゼ」みたいに選択肢を好きなだけ追加できるようにしてほしいな。\n\nできたアプリのURLをシェアしてみんなが投票したら、どの選択肢にいま何票入ってるかが、リアルタイムで全員の画面に反映される感じ！パッと見てわかる簡単なデザインにしてね。', url: "https://miniapplify.web.app/?id=fBhpCPGkZoCmapIRein3" },
    { id: '3', title: '神様のいうとおり', recipe: '罰ゲームとか掃除当番を決めるときに使える、おもしろい抽選アプリが作りたい！\n\n最初に、抽選したい人の名前とかお題（「〇〇君」「〇〇さん」とか）を、好きなだけリストに追加できるようにしてほしい。\n\n準備ができたら、真ん中にある「抽選スタート！」みたいなでっかいボタンを押すの。そしたら、リストの中からランダムで選ばれた１つが「当選！」って感じで、画面にバーン！って大きく表示されるようにして。ガチャみたいでドキドキする感じだと最高！', url: "https://miniapplify.web.app/?id=tm2rM3f6YWllaJqJVvTq" },
    { id: '4', title: '部活カレンダー打刻帳', recipe: '部活メンバー専用の、ゆるい勤怠アプリが作りたい！\n\nアプリを開いたら、まず自分の名前がわかるようにしてほしいな。で、「練習開始」「練習終了」っていうボタンをタップするだけで、その日の時間を記録できるようにして。\n\n今週の合計練習時間が、自分だけじゃなくて部員みんなの分も棒グラフみたいな感じでパッと見てわかるようにしたい！あと、毎月一番練習した人が「今月のMVP」みたいに自動で発表されるまでできると尚可。', url: "https://miniapplify.web.app/?id=FGbz9TOs12fph8aS9o6n" },
    { id: '5', title: '秘密のメッセージ', recipe: '仲良しグループだけで使える、秘密の交換日記みたいなアプリがほしい！\n\n最初に、私たちだけの「合言葉」を決められるようにして。その合言葉を知ってる人じゃないと、絶対に中身を読めないようにしてほしいな。\n\n正しい合言葉を入力した人だけが、メッセージを書き込んだり、読んだりできる特別な掲示板みたいな感じ！これなら、先生とかには見られたくない内緒話も安心してできるから、作ってくれると嬉しい！\n合言葉を入れて入室するときに、名前も入力して名前を固定したい。', url: "https://miniapplify.web.app/?id=KGpYADQCHaD5EQ0H1g7T" },
    { id: '6', title: '推しごとカレンダー', recipe: '推し活が忙しい人のための「推しごとカレンダー」を作りたい！\n\n推しの誕生日、ライブの日、グッズ発売日みたいな大事な予定を登録できて、「ライブまであと〇日！」みたいに自動でカウントダウンしてくれる機能がほしいな。\n\n作ったカレンダーは、同じ推しを応援してる友達とLINEとかで簡単に共有できるようにして！みんなで「もうすぐだね！」ってワクワク感を高めあえるような、見てて楽しいデザインだと嬉しい！', url: "https://miniapplify.web.app/?id=QNBjaPA0isScV1SqCbFQ" },
    { id: '7', title: '会議室サーチ', recipe: '会社の会議室の空き状況が、自分のスマホでひと目でわかるアプリが欲しいです。\n\n「A会議室：使用中」「B会議室：空室」みたいに、全フロアの部屋のステータスがリアルタイムで一覧表示される感じが理想ですね。\n急な打ち合わせが入ったときに、わざわざ歩き回らなくてもすぐに空いてる部屋を見つけられるようにしたいんです。\n\nもしできれば、空室を見つけたら「利用開始」ボタンを押すと、その部屋が「使用中」に切り替わるような簡易的な予約機能もあると、すごく業務が捗りそうです。\n\n会議室はA室、B室、C室、D室、E室の5つですので初期データにいれてください。', url: "https://miniapplify.web.app/?id=afe3mGpuj1HGkpPcwBIk" },
    { id: '8', title: '研究室掃除ローテ', recipe: '研究室の掃除ローテアプリ\n\nタスクの種類（3種類）：\n- 机・椅子の整理\n- ゴミ箱の片付け\n- ホワイトボード清拭\n\n毎日自動的にローテーションで各タスクを割り当て、各ユーザーに公平に分配。3名に割り振り、割り振られないユーザーも発生する。タスク完了時に写真をアップロードして完了状況を確認可能。', url: "https://miniapplify.web.app/?id=rVu88kE9ljeprJPnbPWw" },
    { id: '9', title: 'サブスク見直し', recipe: 'Netflix、Adobe、Spotifyなどの使用頻度を分析。月数回使わないサービスを見つけて、無駄な支払いを削減。\n\nサブスクサービスの登録機能（名称設定、月額費用設定）。毎週、各サービスを使ったかをチェック。月に1回以下しか使っていないサービスを警告。月額費用の表示。', url: "https://miniapplify.web.app/?id=ezvFhOqHlMTxSbkgXHeK" },
    { id: '10', title: 'みんなで大喜利バトル', recipe: '毎週のお題で笑いを競う大喜利アプリ。みんなでボケを投稿して、いいねでランキング！「朝起きたら〇〇になっていた」みたいなテーマで、仲間との爆笑バトルを楽しもう。', url: "https://miniapplify.web.app/?id=r5snTdTTx4eG0gj6XSrV" },
    { id: '11', title: '新ことわざメーカー', recipe: '新しいことわざを創造。「急がば回れ道も楽し」など、努力・友情・知恵をテーマに古典と現代を融合したオリジナル格言を生成。\n\nユーザーはキーワードを入力するだけ。古典的な言い回しをすることでそれっぽさを演出。', url: "https://miniapplify.web.app/?id=Z2rrn3tMBf5h1hOo3sAp" },
    { id: '12', title: 'トレカ交換アプリ', recipe: 'トレカ（トレーディングカード）を友達と交換するアプリ\n\n自分の名前の登録、自分が欲しいカードの登録、自分が出せるカードの登録。カードの情報は名前のみ。他のメンバーの登録状況の閲覧、合致する組み合わせを表示。', url: "https://miniapplify.web.app/?id=t7wxIe9x2sRrvQJtG3f9" },
    { id: '13', title: '週末メニュー投票', recipe: '家族で今週末の料理を決める投票アプリ。オムライス、スパゲッティ、ピザなど、投票して楽しく食事計画。料理は登録したり削除もできる。投票は名前を登録してひとり１個まで。投票内容をクリアもできる。投票は楽しく操作できるイメージで！', url: "https://miniapplify.web.app/?id=Twqn3nzt3MUzmABR16Qa" },
    { id: '14', title: '家事当番スコアボード', recipe: '家族の家事をゲーム化。ポイント制でランキング表示し、みんなが楽しく家事に参加できる仕組みを作る。', url: "https://miniapplify.web.app/?id=UAnkdQNpi2x3YLphl7op" },
    { id: '15', title: 'ケンカ冷却タイマー', recipe: '喧嘩後のクールダウン時間を管理。冷静になるためのいくつもアドバイスがアプリ上部にゆっくり次々と現れ、心穏やかに、建設的な仲直りをサポート。', url: "https://miniapplify.web.app/?id=ZkR1flj05P3IXU7zXZHy" },
    { id: '16', title: '今週のデートスピン', recipe: 'デートのマンネリ化を防ぐルーレットアプリ。イタリアン、映画、公園散歩など、多彩なデートプランからランダム選択。', url: "https://miniapplify.web.app/?id=fH1kWM4rvfm2pHaVtX45" },
    { id: '17', title: '週イチ感謝メモ', recipe: '交互に「ありがとう」を記録。毎週交代で相手への感謝の気持ちを言葉にして、日常の小さな幸せを大切に。', url: "https://miniapplify.web.app/?id=blnnb7vDlOIHQRzHKkTl" },
    { id: '18', title: '週イチランチローテーション', recipe: '毎週のランチ店選びを自動化。予算とジャンルを設定して、公平なローテーションで新しいお店を発見。', url: "https://miniapplify.web.app/?id=3ICE7mDwnZsf8SHQbl6E" },
    { id: '19', title: '週末予定シェアリスト', recipe: 'みんなの空き時間を可視化。土日の予定を共有して、全員が参加できる時間を簡単に見つけるアプリ。', url: "https://miniapplify.web.app/?id=JTt05Ixf5n9dFlPFlV0M" },
    { id: '20', title: 'スマート割り勘計算機', recipe: 'グループの会計を瞬時に分割。先輩割増や誕生日無料など、仲間内のルールも反映できるスマートな割り勘アプリ', url: "https://miniapplify.web.app/?id=rokJ0SNyy6c239avW8IK" },
    { id: '21', title: 'クッキー叩きゲーム', recipe: 'アプリ名は「クッキー叩きゲーム」いわゆるクッキークラッカーゲーム。クリックのアニメーションが楽しい。自動クリックもアニメーションで気持ちよく。スマホの狭い画面でもストレスなく遊べるようにクッキーは上部に配置して。', url: "https://miniapplify.web.app?id=Re87v4PS4URAqqGlO9a2" }
  ];

  // 21個からランダムに8個を選択
  const getRandomRecipeExamples = () => {
    const shuffled = [...allSampleApps].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 8);
  };

  // レシピ例（毎回ランダムに8個）
  const [recipeExamples, setRecipeExamples] = useState([]);

	/**
	 * Toru: レシピをリロードしても保存されている値を復元するように変更
	 */
	// 初期化時にlocalStorageからレシピを復元とランダムサンプル設定
  useEffect(() => {
    const savedRecipe = localStorage.getItem('recipeText');
    if (savedRecipe) {
      setRecipe(savedRecipe);
    }
    
    // 初回ロード時にランダムなサンプルを設定
    setRecipeExamples(getRandomRecipeExamples());
  }, []);

  // recipeが変更されたときにlocalStorageに保存
  useEffect(() => {
    if (recipe) {
      localStorage.setItem('recipeText', recipe);
    }
  }, [recipe]);

	/**
	 *  Toru: 最大時間監視の2重タイマーが複数のバグを発生させていたため、リファクタしました。
	 *  主に、creatingStartTimeを使うことにより、アプリ作成時からの経過時間をリロードを跨いで有効に。
	 *  creatingAppStartTimは、HomeScreenのローディング表示にも利用。
	 */

	// テストモード用のポーリングキャンセル
  useEffect(() => {
    if (testMode === 'CancelPolling') {
      cleanupPolling();
    }
  }, [testMode]);

  // ここでは前景のみポーリングの実装は、useApp()の後に配置（依存解決のため）

  // ユーザーIDの状態監視
  useEffect(() => {
    // ユーザーIDが変更された時の処理は特になし
    // これは削除された useLiff 呼び出しから移行したフックの順序を維持するため
  }, [userId]);
  
  // テスト用データ
  const testApps = [
    { id: 'test1', title: 'テスト用アプリ1', description: 'テスト用の説明文1', likes: 5 },
    { id: 'test2', title: 'テスト用アプリ2', description: 'テスト用の説明文2', likes: 10 },
    { id: 'test3', title: 'テスト用アプリ3', description: 'テスト用の説明文3', likes: 15 }
  ];

  // アプリ操作フック
  const {
    apps,
    loading: appLoading,
    error: appError,
    createApp: createAppApi,
    removeApp,
    getApp,
    addLike,
    removeLike,
    loadApps
  } = useApp(userId);

  // ローカルストレージからローディング状態を復元 + 前景のみポーリング（useApp後に配置）
  useEffect(() => {
    const creatingAppId = localStorage.getItem('creatingAppId');

    // 作成開始時間を取得（なければ現在時刻を使用）
    const creatingStartTime = parseInt(localStorage.getItem('creatingStartTime') || Date.now());

    if (!creatingAppId) return;

    // IDが存在するときのみローディング状態にする
    setIsCreatingApp(!!creatingAppId);
    creatingIdRef.current = creatingAppId;

    let timeoutId = null;
    let stopped = false; // クリーンアップ/非表示で停止したかどうか

    const MAX_TIME = 5 * 60 * 1000; // 5分のタイムアウト
    const INTERVAL_MS = 5000; // 通常ポーリング間隔

    const stopPolling = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const scheduleNext = () => {
      stopPolling();
      if (stopped) return;
      if (document.hidden) return; // 前景のみ
      if (reconcilingRef.current) return; // 和解中はポーリング停止
      timeoutId = setTimeout(tick, INTERVAL_MS);
    };

    const finalizeAsTimeout = async () => {
      // タイムアウト時はまず和解チェックで最終状態を優先確定
      await reconcileCreationState();
      // まだ作成中扱いなら中立モーダルを案内
      if (localStorage.getItem('creatingAppId')) {
        setIsCreatingApp(false);
        setShowLongWaitModal(true);
      }
    };

    const tick = async () => {
      if (stopped) return;
      if (document.hidden) return; // バックグラウンドでは処理しない
      if (reconcilingRef.current) { scheduleNext(); return; }

      try {
        const elapsedTime = Date.now() - creatingStartTime;
        console.log(`ポーリング中 (経過: ${Math.round(elapsedTime / 1000)}秒)`);

        if (elapsedTime >= MAX_TIME) {
          finalizeAsTimeout();
          return;
        }

        const appData = await api.getAppById(creatingAppId, userId);
        if (stopped) return; // レース回避

        // まだドキュメントが存在しない（POSTが中断した可能性）場合は一度だけ再送を試みる
        if (!appData) {
          try {
            if (!localStorage.getItem('creatingReposted') && !document.hidden) {
              const recipeText = localStorage.getItem('recipeText') || '';
              const sharedStored = localStorage.getItem('creatingShared') || 'true';
              const displayName = localStorage.getItem('user_public_name') || (publicName || '匿名');
              if (recipeText && userId && creatingAppId) {
                console.log('Reposting createApp after not-found appId (once)');
                await api.createApp(recipeText, userId, creatingAppId, displayName, sharedStored);
                localStorage.setItem('creatingReposted', '1');
              }
            }
          } catch (e) {
            console.warn('Repost createApp failed:', e?.message || e);
          }
          scheduleNext();
          return;
        }

        if (appData && appData.content) {
          setFailureMessage(null);
          setShowLongWaitModal(false);
          setCompletionMessage('アプリ作成が完了しました！');

          if (userId && loadApps) {
            try {
              await new Promise((resolve) => setTimeout(resolve, 1000));
              await loadApps();
              console.log('Apps list reloaded after app creation');
            } catch (err) {
              console.error('Error loading apps after creation:', err);
            }
          }

          cleanupPolling();
          return;
        }

        scheduleNext();
      } catch (error) {
        console.error('アプリデータの取得中にエラーが発生しました:', error);
        scheduleNext();
      }
    };

    const startPolling = (immediate = false) => {
      stopped = false;
      stopPolling();
      if (document.hidden || reconcilingRef.current) return;
      if (immediate) tick(); else scheduleNext();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else if (!stopped) {
        startPolling(true); // 復帰時は即時チェック
      }
    };

    const handlePageShow = () => {
      if (!stopped) startPolling(true);
    };

    const handlePageHide = () => {
      stopPolling();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('pagehide', handlePageHide);

    startPolling(true); // 初回開始（前景のみ）

    return () => {
      stopped = true;
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [isCreatingApp, userId, loadApps, reconciling]);
  
  // テストモード用の関数と値
  const testRemoveApp = (id) => {
    console.log('テストモード: アプリを削除', id);
    return Promise.resolve(true);
  };
  
  const testAddLike = (id) => {
    console.log('テストモード: いいねを追加', id);
    return Promise.resolve(20);
  };
  
  const testRemoveLike = (id) => {
    console.log('テストモード: いいねを取り消す', id);
    return Promise.resolve(10);
  };
  
  // テストモードかどうかに応じてデータと関数を切り替える
  const appsToUse = testMode === 'AppListScreen' ? testApps : apps;
  const removeAppToUse = testMode === 'AppListScreen' ? testRemoveApp : removeApp;
  const addLikeToUse = testMode === 'AppListScreen' ? testAddLike : addLike;
  const removeLikeToUse = testMode === 'AppListScreen' ? testRemoveLike : removeLike;

  // URLパラメータのアプリIDからアプリを読み込む
  const [isLoadingApp, setIsLoadingApp] = useState(false);
  const [loadedAppId, setLoadedAppId] = useState(null);

  useEffect(() => {
    // 現在のappIdと以前読み込んだappIdが異なり、かつロード中でなければ実行
    if (appId && getApp && appId !== loadedAppId && !isLoadingApp) {
      console.log('[DEBUG] Loading app from URL parameter, appId:', appId);
      setIsLoadingApp(true);
      
      const loadAppFromUrl = async () => {
        try {
          const appData = await getApp(appId);
          console.log('[DEBUG] App data loaded:', appData?.title);
          
          if (appData) {
            setSelectedApp(appData);
            setCurrentScreen('app');
          } else {
            console.log('[DEBUG] App not found');
            setCurrentScreen('notFound');
          }
        } catch (error) {
          console.error('[DEBUG] Error loading app:', error);

          // トライアル期限切れエラーの場合
          if (error.code === 'TRIAL_EXPIRED') {
            alert(error.message || 'このアプリは利用期限が切れました。\n\n有料プランにアップグレードすると、過去のアプリも含めて制限なく利用できます。');
            setCurrentScreen('home');
          } else {
            setCurrentScreen('notFound');
          }
        } finally {
          setLoadedAppId(appId);
          setIsLoadingApp(false);
        }
      };
      
      loadAppFromUrl();
    }
  }, [appId, getApp, loadedAppId, isLoadingApp]);

  // ログイン成功後の画面遷移
  useEffect(() => {
    if (authCheckDone && isLoggedIn && !liffLoading) { // Ensure authCheckDone is true
      const savedScreen = localStorage.getItem('returnToScreen');
      if (savedScreen) {
        setCurrentScreen(savedScreen);
        localStorage.removeItem('returnToScreen');
      }
    }
  }, [authCheckDone, isLoggedIn, liffLoading, setCurrentScreen]); // Added authCheckDone and setCurrentScreen
  
  // アプリIDからアプリを読み込む
  useEffect(() => {
    // Ensure LIFF is initialized and auth status is known for getApp
    if (authCheckDone && redirectAppId && currentScreen === 'loading' && getApp) {
      const loadAppById = async () => {
        try {
          const appData = await getApp(redirectAppId);
          if (appData) {
            setSelectedApp(appData);
            setCurrentScreen('app');
          } else {
            setCurrentScreen('notFound');
          }
        } catch (error) {
          console.error('Error loading app:', error);

          // トライアル期限切れエラーの場合
          if (error.code === 'TRIAL_EXPIRED') {
            alert(error.message || 'このアプリは利用期限が切れました。\n\n有料プランにアップグレードすると、過去のアプリも含めて制限なく利用できます。');
            setCurrentScreen('home');
          } else {
            setCurrentScreen('notFound');
          }
        }
      };
      
      loadAppById();
    }
  }, [redirectAppId, currentScreen, getApp, authCheckDone]); // Added authCheckDone

  // Automatically trigger login for root access if not logged in
  useEffect(() => {
    if (authCheckDone && currentScreen === 'home' && !isLoggedIn && !redirectAppId) {
      // console.log('[DEBUG] Auto-triggering login for root access');
      handleLogin();
    }
  }, [authCheckDone, currentScreen, isLoggedIn, redirectAppId, login]); // handleLogin's dependencies (login, redirectAppId) are included

  // Automatically trigger login for /billing access if not logged in
  useEffect(() => {
    if (authCheckDone && currentScreen === 'plan' && !isLoggedIn) {
      console.log('[DEBUG] /billing accessed without login, saving return path and triggering login');
      // Save the screen to return to after login
      localStorage.setItem('returnToScreen', 'plan');
      // Trigger login
      handleLogin();
    }
  }, [authCheckDone, currentScreen, isLoggedIn, login]); // handleLogin's dependencies (login) are included

  //=== ポーリング（作成中状態）のクリーンアップ
  const cleanupPolling = () => {
    setCreatingAppIdLS(null);
    localStorage.removeItem('creatingStartTime');
    isCreatingRef.current = false; // 同期フラグもリセット
    setIsCreatingApp(false);
    setShowLongWaitModal(false);
  };

  // 最終手段として全タイマーを停止（外部で残存するポーリングも停止）
  const stopAllTimers = () => {
    try {
      const lastTimeout = window.setTimeout(() => {}, 0);
      for (let i = 0; i <= lastTimeout; i++) {
        window.clearTimeout(i);
        window.clearInterval(i);
      }
    } catch (_) {}
  };
  
  // アプリ作成失敗時のコールバック
  const handleAppFailed = (errorMsg) => {
    console.error('App creation failed:', errorMsg);
    setCurrentRunId(null);
    cleanupPolling();
    setFailureMessage(`アプリ作成に失敗しました: ${errorMsg}`);
  };

  // 復帰時の和解チェック: 完成/失敗を優先確定。超過時は中立モーダル、未満は継続。
  const reconcileCreationState = async () => {
    const staleId = localStorage.getItem('creatingAppId');
    if (!staleId) return;

    const TIMEOUT_MS = 5 * 60 * 1000;
    let startAt = Number(localStorage.getItem('creatingStartTime') || 0);
    const setStartTimeIfAbsent = (candidateMillis) => {
      try {
        const has = !!localStorage.getItem('creatingStartTime');
        if (!has && candidateMillis && Number.isFinite(candidateMillis)) {
          localStorage.setItem('creatingStartTime', String(candidateMillis));
          startAt = candidateMillis;
        }
      } catch (_) {}
    };

    setReconciling(true);
    try {
      const app = await api.getAppById(staleId, userId);
      if (app && (app.content || app.status === 'completed')) {
        setShowLongWaitModal(false);
        setFailureMessage(null);
        cleanupPolling();
        setCompletionMessage('アプリ作成が完了しました！');
        try { if (userId && loadApps) await loadApps(); } catch (_) {}
        return;
      }
      if (app && app.status === 'failed') {
        const msg = app.errorMessage || 'アプリ作成に失敗しました（コンプライアンス等により中断）';
        try { localStorage.setItem(`failedAck:${staleId}`, String(Date.now())); } catch (_) {}
        setShowLongWaitModal(false);
        setCompletionMessage(null);
        cleanupPolling();
        setFailureMessage(msg);
        return;
      }
      if (!startAt && app && app.created_at) {
        const t = new Date(app.created_at).getTime();
        if (!Number.isNaN(t)) setStartTimeIfAbsent(t);
      }

      // 補助: 最新一覧で確定を試みる
      if (authCheckDone && userId) {
        try {
          const res = await api.getLatestApps(userId);
          const list = Array.isArray(res?.apps) ? res.apps : [];
          const entry = list.find(a => a.id === staleId);
          if (entry) {
            if (entry.content || entry.status === 'completed') {
              setShowLongWaitModal(false);
              setFailureMessage(null);
              cleanupPolling();
              setCompletionMessage('アプリ作成が完了しました！');
              try { if (userId && loadApps) await loadApps(); } catch (_) {}
              return;
            }
            if (entry.status === 'failed') {
              const msg = entry.errorMessage || 'アプリ作成に失敗しました（コンプライアンス等により中断）';
              try { localStorage.setItem(`failedAck:${staleId}`, String(Date.now())); } catch (_) {}
              setShowLongWaitModal(false);
              setCompletionMessage(null);
              cleanupPolling();
              setFailureMessage(msg);
              return;
            }
            if (!startAt && entry.created_at) {
              const t2 = new Date(entry.created_at).getTime();
              if (!Number.isNaN(t2)) setStartTimeIfAbsent(t2);
            }
          }
        } catch (_) { /* ignore */ }
      }

      // 判定不能
      const effectiveStart = startAt || Date.now();
      const elapsed = Date.now() - effectiveStart;
      if (elapsed >= TIMEOUT_MS) {
        setIsCreatingApp(false);
        setShowLongWaitModal(true);
      } else {
        setStartTimeIfAbsent(effectiveStart);
        // ID が存在する場合にのみ継続ローディング
        if (creatingIdRef.current) setIsCreatingApp(true); else setIsCreatingApp(false);
      }
    } catch (_) {
      const effectiveStart = startAt || Date.now();
      const elapsed = Date.now() - effectiveStart;
      if (elapsed >= TIMEOUT_MS) {
        setIsCreatingApp(false);
        setShowLongWaitModal(true);
      } else {
        setStartTimeIfAbsent(effectiveStart);
        if (creatingIdRef.current) setIsCreatingApp(true); else setIsCreatingApp(false);
      }
    } finally {
      setReconciling(false);
    }
  };

  const handleLongWaitGoList = () => {
    setShowLongWaitModal(false);
    // このアプリIDの自動再開を抑止（一覧復元での再開を防ぐ）
    try {
      const id = localStorage.getItem('creatingAppId');
      if (id) localStorage.setItem(`longWaitAck:${id}`, String(Date.now()));
    } catch (_) {}
    cleanupPolling();
    setCurrentScreen('appList');
  };

  // handleLongWaitContinue は廃止（長時間モーダルは一覧への導線のみ）
  
  // シェア機能
  const handleShare = async (app) => {
    console.log('[DEBUG] handleShare called for app:', app?.id); // どのアプリか
    // 絶対URLを使用して共有テキストを生成
    const baseUrl = window.location.origin;
    const shareText = `${baseUrl}?id=${app.id}`;
    console.log('[DEBUG] Sharing text:', shareText); // 生成されたテキスト

    console.log('[DEBUG] User is not logged in, attempting to copy to clipboard...');
    try {
      await navigator.clipboard.writeText(shareText);
      console.log('[DEBUG] navigator.clipboard.writeText successful (not logged in).');
      alert('URLをコピーしました！');
    } catch (error) {
      console.error('[DEBUG] navigator.clipboard.writeText failed (not logged in):', error);
      // フォールバック
      console.log('[DEBUG] Attempting fallback copy method (not logged in).');
      const textArea = document.createElement('textarea');
      textArea.value = shareText;
      textArea.style.position = 'fixed'; // Prevent scrolling to bottom
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        const copied = document.execCommand('copy');
        if (copied) {
          console.log('[DEBUG] Fallback copy method successful (not logged in).');
          alert('URLをコピーしました！ (フォールバック)');
        } else {
          console.error('[DEBUG] Fallback copy method document.execCommand returned false (not logged in).');
          alert('URLのコピーに失敗しました。');
        }
      } catch (execCommandError) {
        console.error('[DEBUG] Fallback copy method failed with error (not logged in):', execCommandError);
        alert('URLのコピーに失敗しました。');
      }
      document.body.removeChild(textArea);
    }
  };
  
  // ホーム画面からアプリ一覧への遷移処理
  const handleGoToAppList = async () => {
    // ログインしている場合はアプリ一覧を再取得してから遷移
    if (isLoggedIn) {
      try {
        await loadApps();  // 最新のアプリ一覧を取得
      } catch (err) {
        console.error('Failed to refresh apps:', err);
      }
      setCurrentScreen('appList');
    } else {
      // 未ログインの場合は未認証画面へ
      setCurrentScreen('unauthenticated');
    }
  };

  // LINEで開く（現在のURLをそのまま保持）
  const openInLINE = () => {
    // LIFFのIDをコンポーネント内で直接参照しない
    const liffUrl = `https://liff.line.me/${import.meta.env.VITE_REACT_APP_LIFF_ID || '2007240462-YK3n6zeO'}`;
    
    // 現在のURLパラメータを保持したまま遷移
    const currentUrl = window.location.href;
    const hasParams = currentUrl.includes('?');
    const redirectUrl = hasParams ? liffUrl + encodeURIComponent(currentUrl.substring(currentUrl.indexOf('?'))) : liffUrl;
    
    window.location.href = redirectUrl;
  };

  // ログイン処理 - URLパラメータを保持
  const handleLogin = () => {
    // 現在のURLパラメータを保持してログイン
    if (redirectAppId) {
      // 画面に戻るようにlocalStorageに保存
      localStorage.setItem('returnToScreen', 'app');
      login(redirectAppId);
    } else {
      // ホーム画面に戻るようにlocalStorageに保存
      localStorage.setItem('returnToScreen', 'home');
      login();
    }
  }
    
    // スムーズスクロール関数
    const scrollToTop = () => {
      // requestAnimationFrameでは動作しないのでsetTimeoutを使用
      setTimeout(() => {
        const scrollContainer = document.querySelector('.app-main-container');
        if (scrollContainer) {
          scrollContainer.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }, 30);
    };
    
    // 実際のアプリ作成処理
    const createApp = async (shared = "true") => {
    // 二重実行防止: 同期的なフラグで即座にチェック
    if (isCreatingRef.current) {
      console.log('Already creating an app, ignoring duplicate request');
      return;
    }

    // 即座にフラグを立てる（同期的、連打を完全に防止）
    isCreatingRef.current = true;

    // ログインチェック
    if (!isLoggedIn) {
      isCreatingRef.current = false; // フラグをリセット
      setCurrentScreen('unauthenticated');
      return;
    }

    // テスト用エラー発生
    if (recipe.includes('ERROR_TEST')) {
      isCreatingRef.current = false; // フラグをリセット
      handleAppFailed('テスト用のエラーが発生しました');
      return;
    }

    // テスト用成功モーダル発生
    if (recipe.includes('SUCCESS_TEST')) {
      isCreatingRef.current = false; // フラグをリセット
      setCompletionMessage('アプリ作成が完了しました！');
      return;
    }

    // テスト用プログレス表示
    if (recipe.includes('PROGRESS_TEST')) {
      setIsCreatingApp(true);
      scrollToTop(); // 画面をトップにスムーズスクロール
      return;
    }

    try {
      // 以前の作成中状態が残っていたらクリア
      cleanupPolling();

      const appId = await api.getNewAppId();
      console.log('[DEBUG] New app ID:', appId, 'shared:', shared);
      // ローカルストレージに保存
      setCreatingAppIdLS(appId);
      localStorage.setItem('creatingShared', String(shared));
      localStorage.removeItem('creatingReposted');

			// 現在の時間を保存（ローディング表示用）
      localStorage.setItem('creatingStartTime', Date.now());
      
      // userIdはuseAuthから直接取得
      if (!userId) {
        throw new Error('ユーザープロファイルが見つかりません');
      }
      
      // 公開表示名（友達に見える名前）を使用（未設定は匿名）
      let userName = publicName || '匿名';
     	
			/**
			 * Toru: ポーリング処理にまとめたいので、createAppApiの返り値を受け取らずにポーリングへ
			 * ただし、API エラーをキャッチするために await する
			 */
      setIsCreatingApp(true);
      scrollToTop(); // 画面をトップにスムーズスクロール

      // バックエンドで生成を実行（結果はポーリングで検知するため、エラーのみハンドル）
      // ここで await して、非ネットワーク系エラー（コンプライアンス・日次上限・その他）を
      // 外側の try/catch に正しく伝播させる。
      try {
        await api.createApp(recipe, userId, appId, userName, shared);
        // 成功時も完了はポーリングで検知するため何もしない
      } catch (err) {
        console.warn('createApp request error (will rely on polling/resume):', err?.message || err);
        // ネットワーク系やバックグラウンド中の中断は失敗扱いにしない
        const msg = String(err?.message || '').toLowerCase();
        const isNet = err?.code === 'ERR_NETWORK' || msg.includes('network') || err?.name === 'CanceledError' || document.hidden;
        if (!isNet) {
          // コンプライアンス・日次上限・その他の明示エラーは上位で処理
          throw err;
        }
        // ネットワーク等の一時エラーは投げずにポーリングで復帰を待つ
      }
      
      
      // const response = await createAppApi(recipe, userId, appId, userName);
      // if (response && appId) {
      //   //setCurrentRunId(response.runId);
      //   // 以下の行をコメントアウト
      //   handleAppCreated(appId);
      //   localStorage.removeItem('creatingAppId'); // AppIdも削除
      // } else {
      //   throw new Error('アプリ作成に失敗しました');
      // }
    } catch (err) {
      console.error('App creation error:', err);
      
      // ネットワーク/中断などは失敗扱いにせず継続（前景復帰時に再送 or タイムアウト）
      const msg = String(err?.message || '').toLowerCase();
      const isTransient = err?.code === 'ERR_NETWORK' || msg.includes('network') || err?.name === 'CanceledError' || document.hidden;
      if (isTransient) {
        // 生成中UIは継続し、ポーリングで復帰を待つ
        if (creatingIdRef.current) setIsCreatingApp(true);
        return;
      }

      // コンプライアンスエラーはレシピ再確認を促す表示
      if (err && err.code === 'COMPLIANCE') {
        const hint = 'コンプライアンスに違反する可能性があります。レシピの内容（認証/決済/外部API/ファイル/個人情報 など）を見直してください。';
        const msg = err.message ? `${hint}\n\n詳細: ${err.message}` : hint;
        alert(msg);
        handleAppFailed(msg);
        // 念のためすべてのタイマーを停止（残存ポーリング解消）
        stopAllTimers();
      } else if (err.code === 'PAYMENT_FAILED') {
        // 決済失敗エラー（402エラー）
        console.log('PAYMENT_FAILED error caught in ApptukuApp:', err);
        const message = `お支払いに問題があります。\n\n` +
          `${err.message || 'クレジットカードの有効期限や残高をご確認ください。'}\n\n` +
          `プラン画面から支払い方法を更新できます。`;

        alert(message);
        handleAppFailed('お支払いに問題があります');
        setCurrentScreen('plan'); // プラン画面へ遷移
        // タイマーとフラグを確実にリセット
        stopAllTimers();
        isCreatingRef.current = false;
      } else if (err.code === 'LIMIT_EXCEEDED') {
        // 利用上限に達した場合（402エラー）
        console.log('LIMIT_EXCEEDED error caught in ApptukuApp:', err);
        const info = err.billingInfo || {};
        const message = `利用上限に達しました。\n\n` +
          `プラン: ${info.plan || '不明'}\n` +
          `今月の使用: ${info.usedThisPeriod || 0}/${info.monthlyQuota || 0}\n` +
          `追加クレジット: ${info.extraCredits || 0}`;

        alert(message + '\n\nプラン画面からアップグレードまたは追加クレジットを購入できます。');
        handleAppFailed('利用上限に達しました');
        setCurrentScreen('plan'); // プラン画面へ遷移
        // タイマーとフラグを確実にリセット
        stopAllTimers();
        isCreatingRef.current = false;
      } else {
        // 復帰直後などは和解に委ねる（和解中/タイムアウト超/長時間モーダル表示中、またはID不一致は黙殺）
        const TIMEOUT_MS = 5 * 60 * 1000;
        const startRaw = localStorage.getItem('creatingStartTime');
        const startAt = startRaw ? Number(startRaw) : NaN;
        const elapsed = Number.isFinite(startAt) ? (Date.now() - startAt) : (TIMEOUT_MS + 1);
        const currentId = creatingIdRef.current;
        if (
          reconcilingRef.current ||
          elapsed >= TIMEOUT_MS ||
          showLongWaitModal ||
          !currentId || currentId !== appId
        ) {
          // 何もしない（和解/誘導に任せる）
        } else {
          alert('アプリ作成に失敗しました: ' + (err?.message || '不明なエラー'));
          handleAppFailed(err?.message || '不明なエラー');
        }
      }

      isCreatingRef.current = false; // フラグをリセット
      setIsCreatingApp(false);
    }
  };

  // リロード/起動時の和解チェック（前景化直後にも呼ばれると良いが、ここでは起動時に）
  useEffect(() => {
    reconcileCreationState();
  }, [authCheckDone, userId]);

  // 前景復帰時にも和解チェックを実行
  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden) {
        reconcileCreationState();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onVisible);
    };
  }, []);

  // サーバー状態からの復元（localStorageが無くても再開/失敗を再現）
  useEffect(() => {
    // 認証チェックが完了していて、ユーザーが判明していることが前提
    if (!authCheckDone || !userId) return;

    // 既にローカルで作成中管理がある場合は、そちらを優先
    const staleId = localStorage.getItem('creatingAppId');
    if (staleId) return;

    (async () => {
      try {
        const res = await api.getLatestApps(userId);
        const list = Array.isArray(res?.apps) ? res.apps : [];

        // 古い stuck creating を無視するための時間窓（例: 10分）
        const PENDING_WINDOW_MS = 10 * 60 * 1000;
        const now = Date.now();

        const isRecent = (d) => {
          if (!d) return false;
          const t = new Date(d).getTime();
          if (Number.isNaN(t)) return false;
          return (now - t) < PENDING_WINDOW_MS;
        };

        // 直近に開始された creating のみを対象（reserved は復元対象から除外）
        const pending = list.find(a => (
          (!a.content || String(a.content).trim() === '') &&
          a.status === 'creating' &&
          isRecent(a.created_at) &&
          !localStorage.getItem(`longWaitAck:${a.id}`)
        ));
        if (pending) {
          setCreatingAppIdLS(pending.id);
          // 復元時の開始時刻はサーバーの created_at を利用（なければ現在時刻）
          try {
            const t = new Date(pending.created_at).getTime();
            const startMillis = Number.isNaN(t) ? Date.now() : t;
            localStorage.setItem('creatingStartTime', String(startMillis));
          } catch (_) {
            localStorage.setItem('creatingStartTime', String(Date.now()));
          }
          // setCreatingAppIdLS で同期済み
          setIsCreatingApp(true);
          return;
        }

        // 直近の failed のみモーダル表示（古い失敗は無視）
        const failed = list.find(a => (!a.content || String(a.content).trim() === '') && a.status === 'failed' && (isRecent(a.updated_at) || isRecent(a.created_at)));
        if (failed) {
          // 同一失敗の再掲を抑止（ACKが最近記録されていればスキップ）
          const ackKey = `failedAck:${failed.id}`;
          const ackAt = Number(localStorage.getItem(ackKey) || 0);
          if (!ackAt || (now - ackAt) >= PENDING_WINDOW_MS) {
            cleanupPolling();
            const msg = failed.errorMessage || 'アプリ作成に失敗しました（コンプライアンスにより中断）';
            setFailureMessage(msg);
            // 今回表示分をACKとして記録（次回以降の再掲防止）
            try { localStorage.setItem(ackKey, String(now)); } catch (_) {}
          }
        }
      } catch (_) {
        // 復元に失敗してもアプリ利用は続行
      }
    })();
  }, [authCheckDone, userId]);

  // レシピ例を選択する関数（現在は使用されていないが、将来の機能拡張のため残す）
  const selectRecipe = (recipeText) => {
    setRecipe(recipeText);
  };

  // ローディング状態とエラー状態の集約
  const loading = (liffLoading || appLoading) && !isCreatingApp;
  const error = liffError || appError;

  if (loading) {
    return <LoadingScreen text="読み込み中..." />;
  }
  
  if (error) {
    return (
      <div className="error-container">
				<AlertTriangle className="error-icon" size={48} />
				<h2 className="error-title">エラーが発生しました</h2>
				<p className="error-message">{error}</p>
				<button
					className="error-button"
					onClick={() => window.location.reload()}
				>
					再読み込み
				</button>
				<button
					className="error-apptuku-button"
					onClick={() => window.location.href = "https://lp.aputsuku.com/"}
				>
					アプつく！へ
				</button>
      </div>
    );
  }
  
  // フッターナビゲーションを表示するかどうかの判定
  const shouldShowFooterNav = () => {
    return ['home', 'appList'].includes(currentScreen);
  };

  // 画面の選択
  const renderScreen = () => {
    switch(currentScreen) {
      case 'loading':
        return (
          <LoadingScreen 
            text="読み込み中..." 
            onCancel={() => {
              console.log('[DEBUG] Manual cancel from loading screen');
              setIsCreatingApp(false);
              setCurrentRunId(null);
              localStorage.removeItem('currentRunId');
              localStorage.removeItem('currentRunIdTimestamp');
              setCurrentScreen('home');
            }}
          />
        );
      case 'login':
        return (
          <LoginScreen 
            appId={redirectAppId}
            onLogin={handleLogin}
            goToHome={() => {
              if (redirectAppId) {
                // アプリIDがある場合は、アプリ画面に直接遷移させる
                const loadSpecificApp = async () => {
                  try {
                    const appData = await api.getAppById(redirectAppId, userId);
                    if (appData) {
                      setSelectedApp(appData);
                      setCurrentScreen('app');
                    } else {
                      setCurrentScreen('notFound');
                    }
                  } catch (error) {
                    console.error('Error loading app:', error);
                    setCurrentScreen('notFound');
                  }
                };
                loadSpecificApp();
              } else {
                // アプリIDがない場合はホーム画面に遷移
                setRedirectAppId(null);
                setCurrentScreen('home');
              }
            }}
          />
        );
      case 'unauthenticated':
        return (
          <UnauthenticatedScreen
            goToHome={() => setCurrentScreen('home')}
            onLogin={handleLogin}
          />
        );
      case 'home':
        // Automatic login for root path is handled by the useEffect above.
        // If not logged in and on root, handleLogin() will be called, leading to a redirect.
        // Otherwise, HomeScreen is rendered.
        return (
          <HomeScreen
            recipe={recipe}
            setRecipe={setRecipe}
            createApp={createApp}
            recipeExamples={recipeExamples}
            selectRecipe={selectRecipe}
            goToAppList={() => handleGoToAppList()}
            isLoggedIn={isLoggedIn}
            isInClient={isInClient}
            openInLINE={openInLINE}
            isCreatingApp={isCreatingApp}
            runId={currentRunId}
            completionMessage={completionMessage}
            clearCompletionMessage={() => setCompletionMessage(null)}
            failureMessage={failureMessage}
            clearFailureMessage={() => setFailureMessage(null)}
            longWaitVisible={showLongWaitModal}
            onLongWaitGoList={handleLongWaitGoList}
            userId={userId}
            goToPlan={() => setCurrentScreen('plan')}
          />
        );
      case 'plan':
        return (
          <PlanScreen
            goToHome={() => setCurrentScreen('home')}
            userId={userId}
          />
        );
      case 'billingSuccess':
        return (
          <BillingSuccessScreen
            goToHome={() => {
              window.history.pushState({}, '', '/');
              setCurrentScreen('home');
            }}
            goToPlan={() => {
              window.history.pushState({}, '', '/billing');
              setCurrentScreen('plan');
            }}
          />
        );
      case 'billingCancel':
        return (
          <BillingCancelScreen
            goToHome={() => {
              window.history.pushState({}, '', '/');
              setCurrentScreen('home');
            }}
            goToPlan={() => {
              window.history.pushState({}, '', '/billing');
              setCurrentScreen('plan');
            }}
          />
        );
      case 'appList':
        return (
          <AppListScreen
            lineId={userId}
            userName={publicName || '匿名'}
            apps={appsToUse}
            deleteApp={removeAppToUse}
            goToHome={() => setCurrentScreen('home')}
            handleShare={handleShare}
            viewApp={(app) => {
              liff.openWindow({ // window.open()はLINE内で使えないのでLIFFを使用する
                url: `${window.location.origin}?id=${app.id}`,
                external: true
              });
            }}
            handleLike={addLikeToUse}
            handleRemoveLike={removeLikeToUse} // いいね取り消し機能を追加
            onEditPublicName={async () => {
              const current = publicName || '';
              const next = window.prompt('公開表示名（友達に見える名前）を入力', current);
              if (next === null) return; // cancel
              const ok = await savePublicName(next);
              if (!ok) {
                alert('1〜24文字で入力してください。');
              }
            }}
            onRefresh={loadApps}
          />
        );
      case 'app':
        return (
          <AppScreen 
            app={selectedApp}
            goToHome={() => setCurrentScreen('home')}
            handleShare={handleShare}
            handleLike={addLike}
            handleRemoveLike={removeLike}
            isLoggedIn={isLoggedIn} // ログイン状態を渡す
            triggerLogin={handleLogin} // ログイン処理を渡す
          />
        );
      case 'notFound':
        return <NotFoundScreen goToHome={() => setCurrentScreen('home')} />;
      default:
        return <HomeScreen />;
    }
  };

  // デバッグパネルコンポーネント（開発環境でのみ表示）
  const DebugPanel = () => {
    if (!import.meta.env.DEV) return null;
    
    const showTestMethods = () => {
      alert(`🐛 表示テストの方法

💡 エラー表示テスト
レシピに「ERROR_TEST」と入力してアプリ生成

💡 成功表示テスト  
レシピに「SUCCESS_TEST」と入力してアプリ生成

💡 プログレス表示テスト
レシピに「PROGRESS_TEST」と入力してアプリ生成`);
    };

    return (
      <button
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 9999,
          backgroundColor: '#ff4444',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          padding: '6px 12px',
          fontSize: '12px',
          fontWeight: 'bold',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          minWidth: 'initial',
          minHeight: 'initial',
        }}
        onClick={showTestMethods}
      >
        表示テスト
      </button>
    );
  };
  
  return (
    <div className="app-main-container">
      <div className="app-content-container">
        {renderScreen()}
        {shouldShowFooterNav() && (
          <FooterNavigation
            currentScreen={currentScreen}
            goToHome={() => setCurrentScreen('home')}
            goToAppList={handleGoToAppList}
            isLoggedIn={isLoggedIn}
            isCreatingApp={isCreatingApp}
          />
        )}
      </div>
      <DebugPanel />
    </div>
  );
};

export default ApptukuApp;
